import { z } from 'zod';
/**
 * Database blocks (productspec §3, §9). A data source is a schema of properties
 * plus a list of views; its rows are ordinary pages whose parent is the data
 * source, and their values live in `pages.properties` keyed by property id.
 */
/** Every property type the prototype offers. `title` is the row's page title. */
export const propertyTypeSchema = z.enum([
    'title',
    'text',
    'number',
    'select',
    'status',
    'multi',
    'person',
    'relation',
    'rollup',
    'date',
    'checkbox',
    'url',
    'email',
    'phone',
    'created',
    'edited',
]);
/** Types that can be grouped by, and so need a finite set of values. */
export const GROUPABLE_PROPERTY_TYPES = ['select', 'status', 'person', 'checkbox'];
/** Chip colours, both for select/status/multi options and for person avatars. */
export const chipColorSchema = z.enum(['grey', 'mint', 'green', 'red', 'amber', 'blue']);
export const propertyOptionSchema = z.object({
    /** The option name as the user typed it; also its stored value. */
    n: z.string().trim().min(1).max(60),
    label: z.string().max(200).optional(),
    c: chipColorSchema,
});
/**
 * Row and column backgrounds. Token names, never raw colours (design/tokens.json),
 * so the stored value is the same in light and dark and the palette cannot be
 * widened through an API call.
 */
export const rowBackgroundSchema = z.enum(['gray', 'yellow', 'green', 'blue', 'pink', 'red']);
export const propertySchema = z.object({
    id: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(80),
    type: propertyTypeSchema,
    /** Emoji shown in the tab, crumbs and column header. Never a data URL. */
    icon: z.string().trim().max(8).optional(),
    options: z.array(propertyOptionSchema).max(60).default([]),
    relation: z.object({ dataSourceId: z.string().uuid() }).strict().optional(),
    rollup: z
        .object({
        relationPropertyId: z.string().min(1).max(64),
        propertyId: z.string().min(1).max(64),
        operation: z.enum(['count', 'sum', 'avg', 'min', 'max', 'values']),
    })
        .strict()
        .optional(),
    /** Column band colour; painted behind the header and every cell. */
    color: rowBackgroundSchema.optional(),
});
/** One property per data source; `title` is required and always shown. */
export const propertiesSchema = z
    .array(propertySchema)
    .max(40)
    .superRefine((properties, ctx) => {
    const ids = new Set();
    for (const [index, property] of properties.entries()) {
        if (ids.has(property.id))
            ctx.addIssue({ code: 'custom', path: [index, 'id'], message: 'Duplicate property id' });
        ids.add(property.id);
        if (property.type === 'relation' && !property.relation)
            ctx.addIssue({
                code: 'custom',
                path: [index, 'relation'],
                message: 'A target database is required',
            });
        if (property.type === 'rollup' && !property.rollup)
            ctx.addIssue({
                code: 'custom',
                path: [index, 'rollup'],
                message: 'A rollup configuration is required',
            });
    }
});
export const layoutSchema = z.enum(['table', 'board', 'calendar', 'list', 'gallery']);
export const filterOperatorSchema = z.enum([
    'has',
    'nhas',
    'eq',
    'neq',
    'gt',
    'lt',
    'before',
    'after',
    'empty',
    'nempty',
    'checked',
    'unchecked',
]);
export const filterRuleSchema = z.object({
    id: z.string().trim().min(1).max(64),
    propId: z.string().trim().min(1).max(64),
    op: filterOperatorSchema,
    /** Free text or a selected option; unused by the valueless operators. */
    val: z.string().trim().max(200).default(''),
});
export const sortRuleSchema = z.object({
    id: z.string().trim().min(1).max(64),
    propId: z.string().trim().min(1).max(64),
    dir: z.enum(['asc', 'desc']),
});
export const calcKindSchema = z.enum([
    'none',
    'count',
    'values',
    'empty',
    'unique',
    'pempty',
    'pfull',
    'sum',
    'avg',
    'median',
    'min',
    'max',
    'range',
    'earliest',
    'latest',
    'checked',
    'unchecked',
    'pchecked',
]);
/** Columns between 80 and 640px, snapped by the client to fixed steps. */
const columnWidthsSchema = z.record(z.string().max(64), z.number().int().min(80).max(640));
export const viewSchema = z.object({
    id: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(80),
    type: layoutSchema,
    filters: z.array(filterRuleSchema).max(10).default([]),
    fmode: z.enum(['and', 'or']).default('and'),
    sorts: z.array(sortRuleSchema).max(5).default([]),
    /** Property ids hidden in this view. The title property cannot be hidden. */
    hidden: z.array(z.string().max(64)).max(40).default([]),
    widths: columnWidthsSchema.default({}),
    /** Property id rows are grouped by, or null. */
    group: z.string().max(64).nullable().default(null),
    calc: z.record(z.string().max(64), calcKindSchema).default({}),
    /** `YYYY-MM`, the month a calendar view is looking at. */
    month: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
        .nullable()
        .default(null),
});
export const viewsSchema = z.array(viewSchema).min(1).max(20);
/**
 * A user's private view settings ("Save for me"). Same fields as the view, so
 * the client can merge them over the shared view in one step.
 */
export const viewSettingsSchema = z.object({
    filters: z.array(filterRuleSchema).max(10).default([]),
    fmode: z.enum(['and', 'or']).default('and'),
    sorts: z.array(sortRuleSchema).max(5).default([]),
    hidden: z.array(z.string().max(64)).max(40).default([]),
    widths: columnWidthsSchema.default({}),
    group: z.string().max(64).nullable().default(null),
    calc: z.record(z.string().max(64), calcKindSchema).default({}),
    month: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
        .nullable()
        .default(null),
});
const uuidSchema = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
/** A data source always hangs under one page: that page decides permissions. */
export const createDataSourceSchema = z.object({
    pageId: uuidSchema,
    name: z.string().trim().max(80).optional(),
});
export const updateDataSourceSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    icon: z.string().trim().min(1).max(64).nullable().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'empty' });
export const putPropertiesSchema = z.object({ properties: propertiesSchema });
export const putViewsSchema = z.object({ views: viewsSchema });
export const putViewSettingsSchema = z.object({ settings: viewSettingsSchema });
/** A row is created inside a data source; `afterId` inserts below that row. */
export const createRowSchema = z.object({
    values: z.record(z.string().max(64), z.unknown()).default({}),
    afterId: uuidSchema.nullable().default(null),
});
/**
 * Writing one row: a cell (`propId` + `value`), the row background (`color`)
 * or a full wipe (`clear`). `propId` is validated against the property's type
 * by the service, so a client cannot put an arbitrary object in a number
 * column. At least one of the three must be present.
 */
export const updateRowSchema = z
    .object({
    propId: z.string().trim().min(1).max(64).optional(),
    value: z.unknown().optional(),
    /** Row background; `null` clears it. Ignored when `propId` is given. */
    color: rowBackgroundSchema.nullable().optional(),
    /** Wipe every editable value in the row. */
    clear: z.boolean().optional(),
})
    .refine((value) => value.clear === true || value.propId !== undefined || value.color !== undefined, {
    message: 'nothing_to_write',
});
/**
 * CSV import. The client parses the file and sends the cells keyed by the column
 * *name*; the server matches those names to the schema, so a renamed property
 * cannot write into the wrong column. Rules follow the prototype: names match
 * case-insensitively, a checkbox accepts `yes`/`true`/`1`/`x`, a multi-select
 * splits on commas, and options that do not exist yet are created.
 */
const csvCellSchema = z.string().max(2_000);
export const importRowsSchema = z.object({
    rows: z
        .array(z.record(z.string().min(1).max(80), csvCellSchema))
        .min(1)
        .max(2_000),
});
/** Values are stored on `pages.properties`, keyed by property id. */
export const ROW_COLOR_KEY = 'rowColor';
//# sourceMappingURL=databases.js.map